<template>
  <div>
    <v-snackbar
      v-model="appNotifyState.snackbarVisible"
      :timeout="-1"
      location="bottom"
      color="error"
      multi-line
      max-width="560"
    >
      <div class="text-body-2">{{ snackbarBody }}</div>
      <template #actions>
        <v-btn variant="text" size="small" @click="copyLast">Копировать</v-btn>
        <v-btn variant="text" size="small" @click="openErrorLog">Все ({{ count }})</v-btn>
        <v-btn icon="mdi-close" variant="text" size="small" aria-label="Закрыть" @click="dismissErrorSnackbar" />
      </template>
    </v-snackbar>

    <v-dialog v-model="appNotifyState.logDialogOpen" max-width="960" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Все ошибки ({{ count }})</span>
          <v-btn icon="mdi-close" variant="text" aria-label="Закрыть" @click="closeErrorLog" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <template v-if="count > 0">
            <div
              v-for="(it, idx) in appNotifyState.items"
              :key="it.id"
              class="error-log-entry px-4 py-3"
            >
              <div class="text-caption text-medium-emphasis mb-1">
                {{ idx + 1 }}. {{ formatTime(it.at) }}
              </div>
              <div class="error-log-body text-body-2">{{ it.message }}</div>
            </div>
          </template>
          <div v-else class="pa-4 text-medium-emphasis">Список пуст</div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn color="primary" variant="tonal" :disabled="count === 0" @click="copyAll">Копировать всё</v-btn>
          <v-btn variant="text" :disabled="count === 0" @click="clearErrorLog">Очистить</v-btn>
          <v-spacer />
          <v-btn variant="text" @click="closeErrorLog">Закрыть</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
export default { name: 'AppGlobalNotify' };
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { Clipboard } from 'v-clipboard';
import {
  appNotifyState,
  openErrorLog,
  closeErrorLog,
  clearErrorLog,
  dismissErrorSnackbar,
} from '@/utils/appNotify';
import { formatErrorLogForCopy, truncateForSnackbar } from '@/utils/appErrorLog';

const SNACK_SINGLE = 200;
const SNACK_LAST_IN_MULTI = 100;

const count = computed(() => appNotifyState.items.length);

const snackbarBody = computed(() => {
  const n = count.value;
  const last = appNotifyState.items[n - 1]?.message ?? '';
  if (n <= 1) {
    return truncateForSnackbar(last, SNACK_SINGLE);
  }
  return `Ошибок: ${n}. Последняя: ${truncateForSnackbar(last, SNACK_LAST_IN_MULTI)}`;
});

function formatTime(at: number): string {
  return new Date(at).toLocaleString();
}

async function copyLast(): Promise<void> {
  const last = appNotifyState.items[appNotifyState.items.length - 1]?.message;
  if (last) {
    await Clipboard.copy(last);
  }
}

async function copyAll(): Promise<void> {
  if (count.value === 0) {
    return;
  }
  await Clipboard.copy(formatErrorLogForCopy(appNotifyState.items));
}
</script>

<style scoped>
/* Без v-list-item: у VList subtitle/title по умолчанию ellipsis / line-clamp — URL и query обрезались */
.error-log-entry + .error-log-entry {
  border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.error-log-body {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}
</style>
